import { Link } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { PageHero } from '@/components/PageHero';
import { NotifyForm } from '@/components/NotifyForm';
import { Reveal } from '@/components/Reveal';
import { useSeo } from '@/seo/useSeo';

type Question = { q: string; why: string };
type Group = { title: string; questions: Question[] };

const groups: Group[] = [
  {
    title: 'Architecture',
    questions: [
      {
        q: 'Does the monitoring agent require any inbound access at any client site?',
        why: 'If yes, who owns maintaining and auditing that access over time — you or the vendor?',
      },
      {
        q: 'Is your data tenant-isolated at the database, or only by application-level filtering?',
        why: 'Application-only isolation depends on every query remembering to filter correctly, forever.',
      },
      {
        q: 'Are integration secrets encrypted at rest?',
        why: 'Or stored in a config table in plaintext a database dump would expose.',
      },
      {
        q: 'Does the platform validate outbound destinations it calls on your behalf?',
        why: 'Or will it happily fetch whatever URL an admin types into a settings field?',
      },
    ],
  },
  {
    title: 'Data ownership',
    questions: [
      {
        q: 'Can you export your full ticket history, device inventory, and client data on demand?',
        why: 'Without opening a support ticket and waiting on a vendor to hand it back to you.',
      },
      {
        q: 'If you left tomorrow, what would you actually be able to take with you?',
        why: 'A specific answer here beats a vague reassurance every time.',
      },
      {
        q: 'For backup specifically: whose storage does the data live in?',
        why: 'A backup you can\'t get to without the vendor is a single point of failure with good branding.',
      },
    ],
  },
  {
    title: 'On your worst day',
    questions: [
      {
        q: 'If the vendor has an outage, what visibility do you have into your clients\' networks?',
        why: 'A monitoring platform that\'s also your only window into client health has a blind spot exactly when you need it least.',
      },
      {
        q: 'Does an AI feature take autonomous action, or does a human stay in the loop before anything executes?',
        why: '"AI-powered" is not a specification — ask this specifically, of every vendor, including us.',
      },
      {
        q: 'How is a security incident on the vendor\'s own infrastructure disclosed, and on what timeline?',
        why: 'A vague answer here is itself an answer.',
      },
    ],
  },
];

export function BuyersGuide(): JSX.Element {
  useSeo({
    title: "Buyer's guide — evaluating an RMM/PSA platform",
    description:
      'A vendor-neutral checklist for evaluating an RMM + PSA platform: architecture, data ownership, and what happens on your worst day. Ask every vendor these questions, including us.',
    path: '/buyers-guide',
  });

  return (
    <div>
      <PageHero
        eyebrow="Buyer's guide"
        title="The questions that matter more than the feature table."
        subtitle="Every vendor, us included, will hand you a feature-comparison table where their product wins. These are the questions we think actually predict whether a platform works for your team a year in."
      />

      <section className="pb-16 md:pb-24">
        <div className="container-content max-w-[820px]">
          {groups.map((g, gi) => (
            <Reveal key={g.title} delay={gi * 100}>
              <div className="mb-10">
                <h2 className="font-display text-2xl font-bold text-nexus-ink md:text-3xl">{g.title}</h2>
                <div className="mt-5 flex flex-col gap-3">
                  {g.questions.map((item) => (
                    <div key={item.q} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-4">
                      <CheckCircleOutlineIcon fontSize="small" className="mt-0.5 flex-shrink-0 text-nexus-purple-light" />
                      <div>
                        <p className="text-[15px] font-semibold text-nexus-ink">{item.q}</p>
                        <p className="mt-1 text-sm text-white/60">{item.why}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}

          <Reveal delay={300}>
            <div className="rounded-2xl border border-nexus-purple/25 bg-nexus-purple/[0.06] p-6">
              <h2 className="font-display text-lg font-semibold text-nexus-ink">
                We&apos;re building Nexus to answer every one of these well
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-white/75">
                Outbound-only agents, database-enforced tenant isolation, encrypted fail-closed
                secrets, SSRF-guarded outbound calls, and an AI layer that drafts but never
                auto-executes anything consequential. See the{' '}
                <Link to="/security" className="text-nexus-purple-light no-underline hover:underline">
                  trust center
                </Link>{' '}
                for the detail — and we&apos;d rather you ask us these questions directly than take
                a feature table at face value, from us or anyone else. See how these questions apply
                to specific platforms on the{' '}
                <Link to="/compare" className="text-nexus-purple-light no-underline hover:underline">
                  comparison pages
                </Link>
                .
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content">
          <Reveal>
            <div className="rounded-[28px] border border-nexus-purple/35 bg-gradient-to-br from-[#2a1450] to-[#120a26] p-8 text-center md:p-16">
              <h2 className="font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
                Ask us any of these, directly.
              </h2>
              <p className="mx-auto mt-3 max-w-[520px] text-white/70">
                No sales script — join the design-partner cohort and put these questions to the
                team actually building it.
              </p>
              <div className="mt-8 flex justify-center">
                <NotifyForm />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
